import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GONG_SUCCESS_PARAM } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { IntegrationConnectionStatus, IntegrationService, IntegrationServiceAuthKey } from '../../../types';
import ConnectOAuthButton from '../ConnectOAuthButton';
import IntegrationItemLayout from '../IntegrationItemLayout';
import ConnectGongApiKey from './ConnectGongApiKey';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const GONG_CLIENT_ID = process.env.REACT_APP_GONG_CLIENT_ID;
const GONG_OAUTH_SCOPES =
  'api:calls:read:transcript api:workspaces:read api:calls:read:extensive api:settings:scorecards:read api:stats:scorecards api:calls:read:basic api:calls:read:media-url api:library:read api:crm-calls:manual-association:read api:users:read';
const GONG_OAUTH_REDIRECT_URI = `${BACKEND_URL}/integrations/gong/oauth-callback`;

const GONG_ENCODED_REDIRECT_URI = encodeURIComponent(GONG_OAUTH_REDIRECT_URI);
const GONG_ENCODED_SCOPES = encodeURIComponent(GONG_OAUTH_SCOPES);
const GONG_AUTHORIZATION_URL = `https://app.gong.io/oauth2/authorize?response_type=code&client_id=${GONG_CLIENT_ID}&redirect_uri=${GONG_ENCODED_REDIRECT_URI}&scope=${GONG_ENCODED_SCOPES}`;

const GongItem = () => {
  const [connectionStatus, setConnectionStatus] = useState(IntegrationConnectionStatus.DISCONNECTED);
  const gongAuth = useAppSelector((state) => state.auth.organization?.[IntegrationServiceAuthKey.GONG]);

  const [searchParams, setSearchParams] = useSearchParams();
  const gongSuccessParam = searchParams.get(GONG_SUCCESS_PARAM);

  useEffect(() => {
    if (gongSuccessParam === 'false') {
      setConnectionStatus(IntegrationConnectionStatus.FAILED);
    } else if (gongAuth) {
      setConnectionStatus(IntegrationConnectionStatus.CONNECTED);
    } else {
      setConnectionStatus(IntegrationConnectionStatus.DISCONNECTED);
    }
    // We intentionally don't include gongSuccessParam in the dependency array
    // because we only want to run this effect on initial load and not when the param is cleared
  }, [gongAuth]);

  // Cleaning up URL success param after status is updated
  // We do it in a separate effect to make sure the status is updated before cleaning up the URL parameters
  useEffect(() => {
    // Cleanup only if the status is updated and the success param is present
    if (gongSuccessParam && connectionStatus !== IntegrationConnectionStatus.DISCONNECTED) {
      searchParams.delete(GONG_SUCCESS_PARAM);
      setSearchParams(searchParams, { replace: true });
    }
    // We only run this effect when connection status is changed
  }, [connectionStatus]);

  const isConnected = connectionStatus === IntegrationConnectionStatus.CONNECTED;

  const methods = useMemo(
    () => [
      {
        id: 'gong-oauth',
        title: 'Gong OAuth (Preferred)',
        description:
          'FullyRamped will gain read permissions to read data from your Gong instance. User must have Tech Admin role in Gong.',
        content: (
          <ConnectOAuthButton
            service={IntegrationService.GONG}
            authorizationUrl={GONG_AUTHORIZATION_URL}
            disabled={isConnected}
            setConnectionStatus={setConnectionStatus}
          />
        ),
      },
      {
        id: 'gong-api-key',
        title: 'Gong API Key',
        description: (
          <>
            If OAuth is not available, you can provide an API access key instead. See Gong instructions&nbsp;
            <a
              className="underline"
              href="https://help.gong.io/docs/receive-access-to-the-api"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>
            .
          </>
        ),
        content: (
          <ConnectGongApiKey
            gongAuth={gongAuth}
            connectionStatus={connectionStatus}
            setConnectionStatus={setConnectionStatus}
          />
        ),
      },
    ],
    [connectionStatus, gongAuth]
  );

  return (
    <IntegrationItemLayout
      service={IntegrationService.GONG}
      methods={methods}
      connectionStatus={connectionStatus}
      setConnectionStatus={setConnectionStatus}
    />
  );
};

export default GongItem;
